/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable, Injector, Type} from '@angular/core';
import {Observable} from 'rxjs';

import {ApiConfig} from '../../config/api-config';
import {AdnApiClientService} from '../../common/controller/adn-api-client.service';
import {CreativeApiService} from '../../creative/creative-api.service';
import {LibraryCreativeApiService} from '../../library-creative/library-creative-api.service';
import {LineItemApiService} from '../../line-item/line-item-api.service';
import {LineItemTemplateApiService} from '../../line-item/line-item-template-api.service';
import {LayoutApiService} from '../../admin/layout/layout-api.service';
import {TeamApiService} from '../../admin/team/team-api.service';
import {UserApiService} from '../../admin/user/user-api.service';
import {UserProfileApiService} from '../../admin/user-profile/user-profile-api.service';
import {ProductApiService} from '../../admin/product/product-api.service';
import {MarketplaceProductApiService} from '../../admin/marketplace-product/marketplace-product-api.service';
import {NetworkDataApiService} from '../../admin/network/network-data-api.service';
import {NetworkApiService} from '../../admin/network/network-api.service';
import {NetworkProfileApiService} from '../../../components/session/network-profile-api.service';

interface VersionCopyApi {
  get(id: string): Observable<any>;
  save(model: any): Observable<any>;
}

const RESOURCE_APIS: Record<string, Type<VersionCopyApi>> = {
  Creative: CreativeApiService,
  LibraryCreative: LibraryCreativeApiService,
  LineItem: LineItemApiService,
  LineItemTemplate: LineItemTemplateApiService,
  Layout: LayoutApiService,
  Team: TeamApiService,
  User: UserApiService,
  Product: ProductApiService,
  MarketplaceProduct: MarketplaceProductApiService,
  NetworkData: NetworkDataApiService,
  Network: NetworkApiService,
  NetworkProfile: NetworkProfileApiService
};

interface VersionCopyResourceConfig {
  path: string;
  params?: Record<string, any>;
}

const MIGRATED_RESOURCES: Record<string, VersionCopyResourceConfig> = {
  ApiKey: {path: 'apikeys'},
  Blocklist: {path: 'blocklists'},
  ContextServiceConfiguration: {path: 'contextserviceconfigurations'},
  Coupon: {path: 'coupons'},
  CustomEvent: {path: 'customeventtypes'},
  DataExport: {path: 'dataexports'},
  EmailTemplate: {path: 'emailtemplates', params: {showEditableOnly: true}},
  InvoiceTranslation: {path: 'invoicetranslations'},
  LayoutInclude: {path: 'layoutincludes'},
  NotificationPreferences: {path: 'notificationpreferences'},
  Payment: {path: 'payment'},
  PoliticalCampaign: {path: 'politicalcampaigns'},
  PoliticalEntity: {path: 'politicalentities'},
  ReportTranslation: {path: 'reporttranslations'},
  Ruleset: {path: 'rulesets'},
  Segment: {path: 'segments'},
  ThirdPartyAudience: {path: 'thirdpartyaudiences'},
  NetworkInfo: {path: 'networkinfo'},
  ProductFeed: {path: 'productfeed/products'}
};

/**
 * Resource access for the JSON copy tool. API classes retain feature-specific
 * transforms; the remaining entries use the shared Angular transport directly.
 */
@Injectable({providedIn: 'root'})
export class VersionCopyResourceService {
  constructor(
    private readonly client: AdnApiClientService,
    private readonly injector: Injector,
    private readonly apiConfig: ApiConfig
  ) {
  }

  supports(token: string): boolean {
    return !!(MIGRATED_RESOURCES[token] || RESOURCE_APIS[token] || token === 'UserProfile');
  }

  get(token: string, id: string): Observable<any> {
    if (token === 'UserProfile') {
      return this.injector.get(UserProfileApiService).get(id);
    }
    if (RESOURCE_APIS[token]) {
      return this.injector.get(RESOURCE_APIS[token]).get(id);
    }
    const config = MIGRATED_RESOURCES[token];
    if (config) {
      return this.client.request({
        method: 'GET',
        url: this.itemUri(config, id),
        params: config.params
      });
    }
    throw new Error('Unsupported resource: ' + token);
  }

  save(token: string, data: any): Observable<any> {
    if (token === 'UserProfile') {
      return this.injector.get(UserProfileApiService).saveProfile(data);
    }
    if (RESOURCE_APIS[token]) {
      return this.injector.get(RESOURCE_APIS[token]).save(data);
    }
    const config = MIGRATED_RESOURCES[token];
    if (config) {
      return this.client.request({
        method: 'POST',
        url: this.itemUri(config, data.id),
        params: config.params,
        body: data
      });
    }
    throw new Error('Unsupported resource: ' + token);
  }

  private itemUri(config: VersionCopyResourceConfig, id: string): string {
    return this.apiConfig.getVersionedUri(config.path) + '/' + encodeURIComponent(id);
  }
}
